import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Layout from '../common/Layout';
import { lifeEventAPI } from '../../config/lifeEventAPI';
import { getMediaServeUrl } from '../../config/mediaAPI';
import { getStoryQuestions, getStoryExpansions, askQuestion, updateQuestion } from '../../services/curiosityAPI';
import { axiosInstance } from '../../services/api';
import SharingInfoDisplay from '../common/SharingInfoDisplay';
import { getUser, getFamilies } from '../../utils/secureStorage';

// Components
import StoryHeader from './components/StoryHeader';
import StoryTitle from './components/StoryTitle';
import StoryContentTextOnly from './components/StoryContentTextOnly';
import StoryContentCaption from './components/StoryContentCaption';
import MediaGallery from './components/MediaGallery';
import MediaGalleryHero from './components/MediaGalleryHero';
import EngagementBar from './components/EngagementBar';
import ReactionsSection from '../Reactions/ReactionsSection';
import AddMediaPrompt from './components/AddMediaPrompt';
import AddStoryPrompt from './components/AddStoryPrompt';
import CommentInput from './components/CommentInput';

// Sidebar Components
import FamilyConversation from './sidebar/FamilyConversation';
import PeopleTagged from './sidebar/PeopleTagged';
import StoryTimeline from './sidebar/StoryTimeline';
import WisdomCTA from './sidebar/WisdomCTA';
import TimeCapsuleCTA from './sidebar/TimeCapsuleCTA';
import TellStoryCTA from './sidebar/TellStoryCTA';
import RecordStoryCTA from './sidebar/RecordStoryCTA';
import ContributeToStory from './sidebar/ContributeToStory';

import WisdomMomentsList from '../videoactivation/WisdomMomentsList';
import VideoProcessingBadge from '../videoactivation/VideoProcessingBadge';
import SpeakerConfirmationInline from '../videoactivation/SpeakerConfirmationInline';
import VideoTranscript from '../stories/VideoTranscript';
import { getPendingConfirmations } from '../../services/videoActivationAPI';
import './StoryDetail.css';

// Content condition detection
function getContentCondition(story) {
  const hasText = story?.story && story.story.trim().length > 0;
  const hasMedia = story?.media_files && story.media_files.length > 0;

  if (hasText && hasMedia) return 'text-and-media';
  if (hasText && !hasMedia) return 'text-only';
  if (!hasText && hasMedia) return 'media-only';

  return 'text-only'; // fallback
}

// Helper to get current user from secure storage
function getCurrentUser() {
  try {
    return getUser();
  } catch (error) {
    console.error('Error getting user data:', error);
  }
  return null;
}

// Helper to get active family name
function getActiveFamilyName() {
  try {
    const familyId = localStorage.getItem('familyId');
    const families = getFamilies();
    if (families && familyId) {
      const activeFamily = families.find(f => f.id === familyId);
      if (activeFamily && activeFamily.name) {
        return activeFamily.name;
      }
    }
    // Fallback: return first family's name if available
    if (families && families.length > 0 && families[0].name) {
      return families[0].name;
    }
  } catch (error) {
    console.error('Error getting family name:', error);
  }
  return null;
}

function StoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  // Conversation state (questions & expansions from Curiosity API)
  const [questions, setQuestions] = useState([]);
  const [expansions, setExpansions] = useState([]);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [pendingJobId, setPendingJobId] = useState(null);
  const videoRef = useRef(null);

  // Get current user for ownership check
  const currentUser = getCurrentUser();

  // Check if current user is the story owner
  const isOwner = story && currentUser && story.user === currentUser.person_id;

  // Derived state
  const contentCondition = getContentCondition(story);

  const fetchStory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await lifeEventAPI.getEvent(id);
      setStory(response);
    } catch (err) {
      console.error('Error fetching story:', err);
      setError('Failed to load story. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchConversation = useCallback(async () => {
    try {
      setConversationLoading(true);
      const [questionsData, expansionsData] = await Promise.all([
        getStoryQuestions(id),
        getStoryExpansions(id)
      ]);
      setQuestions(questionsData || []);
      setExpansions(expansionsData || []);
    } catch (err) {
      console.error('Error fetching conversation:', err);
      // Non-critical error - don't show to user
    } finally {
      setConversationLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchStory();
    fetchConversation();
  }, [fetchStory, fetchConversation]);

  // Fetch pending speaker confirmations for videos in this story
  useEffect(() => {
    const hasVideos = story?.media_files?.some(m => m.media_type === 'video');
    if (!hasVideos) return;
    getPendingConfirmations()
      .then(data => {
        if (data.job_ids?.length > 0) {
          setPendingJobId(data.job_ids[0]);
        }
      })
      .catch(() => {});
  }, [story]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleEdit = () => {
    navigate(`/stories/${id}/edit`);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: story.title,
        text: story.story?.substring(0, 100) + '...',
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleComment = async (commentData) => {

    // Handle both old string format and new object format
    const text = typeof commentData === 'string' ? commentData : commentData.text;
    const media = typeof commentData === 'object' ? commentData.media : null;

    if (!text?.trim() && !media) {
      return;
    }

    try {
      // If there's media, upload it first
      let mediaFileId = null;
      if (media?.file) {
        const formData = new FormData();
        formData.append('file', media.file);
        formData.append('media_type', media.type);
        formData.append('title', `Comment ${media.type} - ${new Date().toLocaleDateString()}`);

        const uploadResponse = await axiosInstance.post('/media/files/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        mediaFileId = uploadResponse.data.id;
      }

      // Create the question/comment
      const questionPayload = {
        question_text: text || (media ? `[${media.type === 'audio' ? 'Voice' : 'Video'} message]` : ''),
        question_type: 'general'
      };

      // Add media reference if uploaded
      if (mediaFileId) {
        questionPayload.media_file_id = mediaFileId;
      }

      const newQuestion = await askQuestion(id, questionPayload);
      setQuestions(prev => [newQuestion, ...prev]);
    } catch (err) {
      console.error('Error posting comment:', err);
    }
  };

  const handleUpdateQuestion = async (questionId, newText) => {
    if (!newText.trim()) return;

    try {
      const updatedQuestion = await updateQuestion(questionId, {
        question_text: newText
      });
      // Update the question in state
      setQuestions(prev => prev.map(q =>
        q.id === questionId ? { ...q, question_text: newText, ...updatedQuestion } : q
      ));
    } catch (err) {
      console.error('Error updating question:', err);
    }
  };

  const handleAddWisdom = () => {
  };

  const handleCreateTimeCapsule = () => {
  };

  const handleAddMedia = () => {
    navigate(`/stories/${id}/edit`);
  };

  const handleAddStory = () => {
    navigate(`/stories/${id}/edit`);
  };

  const handleMediaContributed = () => {
    // Refresh the story to show new media
    fetchStory();
  };

  const handleTagPerson = () => {
  };

  // Format story data for components
  const formatAuthor = () => {
    // If current user is the owner, show their name
    if (isOwner && currentUser) {
      const userName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim();
      return {
        id: currentUser.person_id || null,
        name: userName || 'You',
        emoji: '👤',
        relationship: 'Creator'
      };
    }

    // For stories by others, use the actual author's name from the API
    if (story?.user_name) {
      return {
        id: story.user || null,
        name: story.user_name,
        emoji: '👤',
        relationship: 'Family'
      };
    }

    // Fallback to current user's name if author info unavailable
    if (currentUser && (currentUser.first_name || currentUser.last_name)) {
      const userName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim();
      return {
        name: userName,
        emoji: '👤',
        relationship: 'Family'
      };
    }

    // Last fallback - try family name
    const familyName = getActiveFamilyName();
    if (familyName) {
      return {
        name: familyName,
        emoji: '👨‍👩‍👦',
        relationship: 'Family'
      };
    }

    return {
      name: 'Family',
      emoji: '👨‍👩‍👦',
      relationship: 'Family'
    };
  };

  const formatTags = () => {
    const tags = [];
    if (story?.event_data?.story_type) {
      tags.push({
        id: '1',
        label: getStoryTypeLabel(story.event_data.story_type),
        emoji: getStoryTypeIcon(story.event_data.story_type),
        color: 'green'
      });
    }
    if (story?.event_data?.people_involved) {
      tags.push({
        id: '2',
        label: story.event_data.people_involved,
        emoji: '👥',
        color: 'blue'
      });
    }
    if (story?.location) {
      tags.push({
        id: '3',
        label: story.location,
        emoji: '📍',
        color: 'gray'
      });
    }
    return tags;
  };

  const formatMedia = () => {
    if (!story?.media_files) return [];
    return story.media_files.map((media, index) => {
      // Use direct S3 URL for videos and audio (redirect-based serve URLs don't work for streaming)
      // Fall back to serve endpoint for images and other types
      const directUrl = media.file_url_debug || media.file;
      const isVideo = (media.media_type === 'video');
      const isAudio = (media.media_type === 'audio');
      const mediaUrl = ((isVideo || isAudio) && directUrl) ? directUrl : getMediaServeUrl(media.id);

      return {
        id: media.id,
        type: media.media_type || 'photo',
        url: mediaUrl,
        thumbnailUrl: mediaUrl,
        order: index
      };
    });
  };

  const formatPeopleTagged = () => {
    // TODO: Get from story data
    return [
      { id: '1', name: 'Max', emoji: '👦' },
      { id: '2', name: 'Buddy', emoji: '🐕' }
    ];
  };

  const getStoryTypeLabel = (value) => {
    const storyTypes = {
      'family_memory': 'Family Memory',
      'personal_experience': 'Personal Experience',
      'historical_event': 'Historical Event',
      'life_lesson': 'Life Lesson',
      'funny_moment': 'Funny Moment',
      'tragic_event': 'Tragic Event',
      'celebration': 'Celebration',
      'other': 'Other'
    };
    return storyTypes[value] || 'Family Memory';
  };

  const getStoryTypeIcon = (value) => {
    const icons = {
      'family_memory': '📝',
      'personal_experience': '🌟',
      'historical_event': '📚',
      'life_lesson': '💡',
      'funny_moment': '😄',
      'tragic_event': '💔',
      'celebration': '🎉',
      'other': '📝'
    };
    return icons[value] || '📝';
  };

  if (loading) {
    return (
      <Layout>
        <div className="story-detail-loading">
          <div className="loading-spinner"></div>
          <p>Loading your story...</p>
        </div>
      </Layout>
    );
  }

  if (error || !story) {
    return (
      <Layout>
        <div className="story-detail-error">
          <div className="error-message">{error || 'Story not found'}</div>
          <button onClick={handleBack} className="btn-back-to-stories">
            Back to Stories
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="story-detail-container">
        <div className="story-detail-layout">
          {/* Left Panel */}
          <div className="story-detail-left-panel">
            <div className="story-detail-card">
              {/* Header Bar */}
              <div className="story-detail-header-bar">
                <button onClick={handleBack} className="back-button">
                  <ArrowLeft size={16} />
                  Back
                </button>
                {isOwner && (
                  <button onClick={handleEdit} className="edit-button">
                    Edit
                  </button>
                )}
              </div>

              {/* Story Header */}
              <StoryHeader
                author={formatAuthor()}
                createdAt={story.date || story.created_at}
                location={story.location}
                privacy={story.is_private ? 'private' : 'public'}
              />

              {/* Story Title */}
              <StoryTitle
                title={story.title}
                tags={formatTags()}
                titleSize={contentCondition === 'text-only' ? 'large' : 'medium'}
              />

              {/* Sharing Info - only for multi-family owners */}
              <SharingInfoDisplay
                sharingScope={story.sharing_scope}
                originFamilyId={story.origin_family}
                sharedWithFamilies={story.shared_with_families}
                isOwner={isOwner}
                className="px-6 pb-2"
              />

              {/* Content Area - varies by condition */}
              {contentCondition === 'text-only' && (
                <>
                  <StoryContentTextOnly content={story.story} />
                  <AddMediaPrompt onClick={handleAddMedia} />
                </>
              )}

              {contentCondition === 'text-and-media' && (
                <>
                  <MediaGallery
                    media={formatMedia()}
                    currentIndex={currentMediaIndex}
                    onIndexChange={setCurrentMediaIndex}
                  />
                  {/* Video processing status + Transcript + Wisdom moments */}
                  {story.media_files?.filter(m => m.media_type === 'video').map(v => (
                    <div key={v.id} className="px-6">
                      <VideoProcessingBadge videoId={v.id} />
                      <VideoTranscript mediaFileId={v.id} />
                      {pendingJobId && (
                        <SpeakerConfirmationInline
                          jobId={pendingJobId}
                          videoMediaFileId={v.id}
                          onComplete={() => setPendingJobId(null)}
                        />
                      )}
                      <WisdomMomentsList videoId={v.id} personName={formatAuthor()} />
                    </div>
                  ))}
                  <StoryContentCaption
                    content={story.story}
                    characterCount={story.story?.length || 0}
                  />
                </>
              )}

              {contentCondition === 'media-only' && (
                <>
                  <MediaGalleryHero
                    media={formatMedia()}
                    currentIndex={currentMediaIndex}
                    onIndexChange={setCurrentMediaIndex}
                  />
                  {/* Video processing status + Transcript + Wisdom moments */}
                  {story.media_files?.filter(m => m.media_type === 'video').map(v => (
                    <div key={v.id} className="px-6">
                      <VideoProcessingBadge videoId={v.id} />
                      <VideoTranscript mediaFileId={v.id} />
                      {pendingJobId && (
                        <SpeakerConfirmationInline
                          jobId={pendingJobId}
                          videoMediaFileId={v.id}
                          onComplete={() => setPendingJobId(null)}
                        />
                      )}
                      <WisdomMomentsList videoId={v.id} personName={formatAuthor()} />
                    </div>
                  ))}
                  <AddStoryPrompt onClick={handleAddStory} />
                </>
              )}

              {/* Engagement Bar */}
              <EngagementBar
                commentCount={0}
                onComment={handleComment}
                onShare={handleShare}
                onAddWisdom={handleAddWisdom}
                onCreateTimeCapsule={handleCreateTimeCapsule}
              />

              {/* Legacy Reactions */}
              <ReactionsSection contentType="story" contentId={id} />

              {/* Comment Input - only for text-and-media */}
              {contentCondition === 'text-and-media' && (
                <CommentInput onSubmit={handleComment} />
              )}

              {/* Reading Stats - only for text-only */}
              {contentCondition === 'text-only' && (
                <div className="reading-stats">
                  <span>1 min read</span>
                  <span className="stat-separator">·</span>
                  <span>{story.story?.length || 0} characters</span>
                  <span className="stat-separator">·</span>
                  <span>Written with love</span>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel (Sidebar) */}
          <div className="story-detail-right-panel">
            {/* Family Conversation */}
            <FamilyConversation
              questions={questions}
              expansions={expansions}
              loading={conversationLoading}
              onReply={() => {}}
              onComment={handleComment}
              onUpdateQuestion={handleUpdateQuestion}
              variant={contentCondition}
            />

            {/* People Tagged */}
            <PeopleTagged
              people={formatPeopleTagged()}
              onTagMore={handleTagPerson}
              variant={contentCondition === 'media-only' ? 'media-only' : 'default'}
            />

            {/* Contribute to Story - shown to non-owners */}
            {!isOwner && (
              <ContributeToStory
                storyId={id}
                storyTitle={story.title}
                onContributed={handleMediaContributed}
              />
            )}

            {/* Conditional CTAs based on content condition */}
            {contentCondition === 'media-only' && (
              <>
                <TellStoryCTA onClick={handleAddStory} />
                <RecordStoryCTA onClick={() => {}} />
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default StoryDetail;
